import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, MapPin, Save, Trash2, Users, AlertTriangle, CheckCircle2, Search, Crosshair, Eye, EyeOff, BoxSelect, CheckSquare, Square } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import TrackPageGoogleMap from '../../components/map/TrackPageGoogleMap';
import { safeJsonFetch, authHeaders } from '../../lib/safeFetch';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

// Dark-red house status flags. Safety = house unsafe for delivery.
const STATUS_TYPES = ['non_deliverable', 'gated', 'safety'];
const STATUS_LABELS = { non_deliverable: 'Non Deliverable', gated: 'Gated Community', safety: 'Safety' };
const emptyStatusSets = () => ({ non_deliverable: new Set(), gated: new Set(), safety: new Set() });

// Transparent drag-capture layer for box select. Sits ABOVE the Google map,
// records a pixel rectangle, and hands it up on mouse-up. While mounted the
// map can't pan and markers can't be clicked because mouse events never
// reach them (google.maps.Map has no mousedown/mouseup events, so a DOM
// overlay is the only reliable way to implement drag-to-select).
const BoxSelectOverlay = ({ onSelect }) => {
  const [drag, setDrag] = useState(null); // {x0, y0, x1, y1} in overlay px
  const ref = useRef(null);

  const pos = (e) => {
    const r = ref.current.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };

  const finish = () => {
    const d = drag;
    setDrag(null);
    if (!d) return;
    const rect = {
      left: Math.min(d.x0, d.x1),
      top: Math.min(d.y0, d.y1),
      right: Math.max(d.x0, d.x1),
      bottom: Math.max(d.y0, d.y1),
    };
    // Ignore accidental clicks (no real drag). Called from the event
    // handler (not inside a state updater) so the parent's setState
    // doesn't fire mid-render.
    if (rect.right - rect.left > 5 && rect.bottom - rect.top > 5) {
      onSelect(rect);
    }
  };

  return (
    <div
      ref={ref}
      className="absolute inset-0 z-10 cursor-crosshair"
      onMouseDown={(e) => {
        e.preventDefault();
        const p = pos(e);
        setDrag({ x0: p.x, y0: p.y, x1: p.x, y1: p.y });
      }}
      onMouseMove={(e) => {
        if (!drag) return;
        const p = pos(e);
        setDrag((d) => (d ? { ...d, x1: p.x, y1: p.y } : d));
      }}
      onMouseUp={finish}
      onMouseLeave={finish}
      data-testid="box-select-overlay"
    >
      {drag && (
        <div
          className="absolute border-2 border-purple-600 bg-purple-500/20 rounded-sm pointer-events-none"
          style={{
            left: Math.min(drag.x0, drag.x1),
            top: Math.min(drag.y0, drag.y1),
            width: Math.abs(drag.x1 - drag.x0),
            height: Math.abs(drag.y1 - drag.y0),
          }}
        />
      )}
    </div>
  );
};

/**
 * AdminMarkJob — admin/auditor-only manual house-marking page.
 *
 * The admin clicks anywhere on the job's delivery zone map to drop a purple
 * house marker. Each mark is held in local "draft" state. When the admin is
 * done, they pick which contributor to credit from the dropdown and click
 * "Save & Credit User". The backend appends the marks to that user's
 * tracking_progress, which propagates to all existing map consumers
 * (mobile users, customer tracking, client portal) on their next poll.
 */
const AdminMarkJob = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();

  const [snapshot, setSnapshot] = useState(null);
  const [contributors, setContributors] = useState([]);
  const [creditUserId, setCreditUserId] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Houses the admin has clicked (drafted as delivered) but NOT saved yet.
  // Nothing hits the backend until "Save deliveries" + credit confirmation.
  const [pendingHouseIds, setPendingHouseIds] = useState(new Set());
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [savingHouses, setSavingHouses] = useState(false);
  // Box-select mode: drag a rectangle on the map to draft every green
  // house inside it at once (Esc or re-click the toggle to exit).
  const [boxSelectMode, setBoxSelectMode] = useState(false);
  // Status-flag mode: when 'non_deliverable' or 'gated' is check-marked,
  // box select + house clicks flag houses DARK RED instead of drafting
  // deliveries. The two buttons are mutually exclusive checkboxes.
  const [statusMode, setStatusMode] = useState(null);
  // Saved status flags (from the snapshot) and local unsaved drafts.
  const [statusHouseIds, setStatusHouseIds] = useState(emptyStatusSets());
  const [pendingStatusIds, setPendingStatusIds] = useState(emptyStatusSets());
  // GPS trails from workers (tracking docs + sessions), toggleable.
  const [showTrails, setShowTrails] = useState(true);
  // Local copy of delivered house IDs so the click-toggle UI can update
  // optimistically without waiting for the next snapshot fetch.
  const [deliveredHouseIds, setDeliveredHouseIds] = useState(new Set());
  // Per-house attribution ({house_id: {user_name, at, admin_name}}) that
  // powers the "Delivered by ..." hover tooltips on yellow dots.
  const [deliveredHouseInfo, setDeliveredHouseInfo] = useState({});
  // True when the contributor list came from the fallback ("all workers")
  // because the job no longer has a live team. Shown as a small notice in
  // the sidebar so the admin knows the credit list is broader than usual.
  const [isPostCompletionFallback, setIsPostCompletionFallback] = useState(false);

  // Imperative handle to the underlying Google Map so we can pan/zoom from
  // the address search box and the "Recenter on zone" button.
  const mapInstanceRef = useRef(null);
  const handleMapReady = useCallback((map) => {
    mapInstanceRef.current = map;
  }, []);

  // Address search
  const [searchInput, setSearchInput] = useState('');
  const [searching, setSearching] = useState(false);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    const [snapRes, contribRes] = await Promise.all([
      safeJsonFetch(`${API}/api/admin/jobs/${jobId}/mark/snapshot`, { headers: authHeaders() }),
      safeJsonFetch(`${API}/api/admin/jobs/${jobId}/mark/contributors`, { headers: authHeaders() }),
    ]);
    if (!snapRes.ok) {
      setError(snapRes.data?.detail || snapRes.error || 'Failed to load job snapshot');
      setLoading(false);
      return;
    }
    setSnapshot(snapRes.data);
    // Seed the local optimistic-toggle set from the snapshot's authoritative
    // list of delivered houses. Subsequent clicks update this Set in place
    // and don't depend on re-fetching the snapshot.
    setDeliveredHouseIds(new Set(snapRes.data?.delivered_house_ids || []));
    setDeliveredHouseInfo(snapRes.data?.delivered_house_info || {});
    setStatusHouseIds({
      non_deliverable: new Set(snapRes.data?.non_deliverable_house_ids || []),
      gated: new Set(snapRes.data?.gated_house_ids || []),
      safety: new Set(snapRes.data?.safety_house_ids || []),
    });
    setPendingStatusIds(emptyStatusSets());
    if (contribRes.ok && contribRes.data?.contributors) {
      setContributors(contribRes.data.contributors);
      const assigned = contribRes.data.contributors.find((c) => c.is_currently_assigned);
      setCreditUserId(assigned?.user_id || contribRes.data.contributors[0]?.user_id || '');
      setIsPostCompletionFallback(!!contribRes.data?.is_post_completion_fallback);
    }
    setLoading(false);
  }, [jobId]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Map click is intentionally a no-op now. Per product spec (Feb 2026),
  // admin marks are placed by clicking a house dot directly (toggle
  // delivered ↔ undelivered) — not by dropping free-floating purple
  // pins on empty land. We keep this callback so the map component
  // still accepts a click handler; it just gets ignored.
  const handleMapClick = useCallback(() => {
    // intentional no-op
  }, []);

  // Click-to-toggle on a house dot.
  //   • green house  → drafted into the pending set (turns yellow locally;
  //     saved only when the admin hits "Save deliveries" and picks who to
  //     credit in the confirmation dialog)
  //   • pending yellow house → removed from the draft (no confirm needed)
  //   • previously SAVED yellow house → instant un-mark (after confirm)
  const handleHouseClick = useCallback(async (house) => {
    // Status-flag mode: clicks flag/un-flag dark-red statuses instead of
    // drafting deliveries.
    if (statusMode) {
      const label = STATUS_LABELS[statusMode];
      if (pendingStatusIds[statusMode].has(house.id)) {
        setPendingStatusIds((prev) => {
          const next = new Set(prev[statusMode]);
          next.delete(house.id);
          return { ...prev, [statusMode]: next };
        });
        return;
      }
      if (statusHouseIds[statusMode].has(house.id)) {
        // eslint-disable-next-line no-alert
        if (!window.confirm(`Remove the ${label} flag from this house?`)) return;
        const res = await safeJsonFetch(`${API}/api/admin/jobs/${jobId}/mark/save-status-houses`, {
          method: 'POST',
          headers: { ...authHeaders(), 'Content-Type': 'application/json' },
          body: JSON.stringify({ status: statusMode, house_ids: [house.id], remove: true }),
        });
        if (!res.ok) {
          toast.error(res.data?.detail || res.error || 'Un-flag failed');
          return;
        }
        setStatusHouseIds((prev) => {
          const next = new Set(prev[statusMode]);
          next.delete(house.id);
          return { ...prev, [statusMode]: next };
        });
        toast.success('Flag removed', { duration: 1200 });
        return;
      }
      const conflict = STATUS_TYPES.find((st) =>
        st !== statusMode && (statusHouseIds[st].has(house.id) || pendingStatusIds[st].has(house.id)));
      if (conflict) {
        toast.info(`Already flagged ${STATUS_LABELS[conflict]} — un-flag it in that mode first.`);
        return;
      }
      if (deliveredHouseIds.has(house.id) || pendingHouseIds.has(house.id)) {
        toast.info('That house is marked delivered — un-mark the delivery first.');
        return;
      }
      setPendingStatusIds((prev) => ({
        ...prev,
        [statusMode]: new Set(prev[statusMode]).add(house.id),
      }));
      return;
    }
    // Deliver mode: dark-red flagged houses aren't toggleable here.
    if (STATUS_TYPES.some((st) => statusHouseIds[st].has(house.id) || pendingStatusIds[st].has(house.id))) {
      toast.info('This house is flagged — check the matching status button to change it.');
      return;
    }
    // Drafted this session — click again to un-draft.
    if (pendingHouseIds.has(house.id)) {
      setPendingHouseIds((prev) => {
        const next = new Set(prev);
        next.delete(house.id);
        return next;
      });
      return;
    }
    const wasDelivered = deliveredHouseIds.has(house.id);
    if (!wasDelivered) {
      // Draft it — backend untouched until Save.
      setPendingHouseIds((prev) => new Set(prev).add(house.id));
      return;
    }
    // Un-marking an already-saved delivery stays instant (with confirm).
    if (!creditUserId) {
      toast.error('Pick a credit user first.');
      return;
    }
    // eslint-disable-next-line no-alert
    if (!window.confirm('Un-mark this delivery?')) return;
    // Optimistic UI update for snappy feedback
    setDeliveredHouseIds((prev) => {
      const next = new Set(prev);
      next.delete(house.id);
      return next;
    });
    const res = await safeJsonFetch(
      `${API}/api/admin/jobs/${jobId}/mark/toggle-house`,
      {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ house_id: house.id, user_id: creditUserId }),
      },
    );
    if (!res.ok) {
      // Roll back optimistic change
      setDeliveredHouseIds((prev) => new Set(prev).add(house.id));
      toast.error(res.data?.detail || res.error || 'Toggle failed');
      return;
    }
    // Keep hover-tooltip attribution in sync without a snapshot refetch.
    setDeliveredHouseInfo((prev) => {
      const next = { ...prev };
      delete next[house.id];
      return next;
    });
    toast.success('Un-marked', { duration: 1200 });
  }, [pendingHouseIds, deliveredHouseIds, creditUserId, jobId, statusMode, statusHouseIds, pendingStatusIds]); // eslint-disable-line react-hooks/exhaustive-deps

  const statusPendingCount = STATUS_TYPES.reduce((n, st) => n + pendingStatusIds[st].size, 0);

  const handleDiscardPending = () => {
    const total = pendingHouseIds.size + statusPendingCount;
    if (total === 0) return;
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Discard all ${total} unsaved mark${total === 1 ? '' : 's'}?`)) return;
    setPendingHouseIds(new Set());
    setPendingStatusIds(emptyStatusSets());
  };

  // Box-select: draft every green house inside the dragged rectangle.
  // Saved-delivered houses are skipped (box never un-marks anything).
  // In status-flag mode the box drafts DARK RED flags instead.
  const handleBoxSelect = useCallback((ids) => {
    if (statusMode) {
      const label = STATUS_LABELS[statusMode];
      const fresh = ids.filter((id) =>
        !deliveredHouseIds.has(id) && !pendingHouseIds.has(id) &&
        !STATUS_TYPES.some((st) => statusHouseIds[st].has(id) || pendingStatusIds[st].has(id)));
      if (fresh.length === 0) {
        toast.info(ids.length === 0 ? 'No houses inside that box.' : 'All houses in that box are already marked or flagged.');
        return;
      }
      setPendingStatusIds((prev) => ({
        ...prev,
        [statusMode]: new Set([...prev[statusMode], ...fresh]),
      }));
      toast.success(`Flagged ${fresh.length} house${fresh.length === 1 ? '' : 's'} as ${label} — hit Save when done`);
      return;
    }
    const fresh = ids.filter((id) =>
      !deliveredHouseIds.has(id) && !pendingHouseIds.has(id) &&
      !STATUS_TYPES.some((st) => statusHouseIds[st].has(id) || pendingStatusIds[st].has(id)));
    if (fresh.length === 0) {
      toast.info(ids.length === 0 ? 'No houses inside that box.' : 'All houses in that box are already marked.');
      return;
    }
    setPendingHouseIds((prev) => new Set([...prev, ...fresh]));
    toast.success(`Drafted ${fresh.length} house${fresh.length === 1 ? '' : 's'} — hit Save deliveries when done`);
  }, [deliveredHouseIds, pendingHouseIds, statusMode, statusHouseIds, pendingStatusIds]);

  // Convert the dragged pixel rectangle into house IDs using the map's
  // current viewport. Houses are projected lat/lng → container pixels with
  // the exact Web Mercator formula Google Maps renders with, so containment
  // is pixel-accurate at any zoom.
  const handleBoxPixelSelect = useCallback((rect) => {
    const map = mapInstanceRef.current;
    const houses = snapshot?.cached_house_locations || [];
    if (!map || houses.length === 0) return;
    const bounds = map.getBounds();
    if (!bounds) return;
    const div = map.getDiv();
    const W = div.clientWidth;
    const H = div.clientHeight;
    const ne = bounds.getNorthEast();
    const sw = bounds.getSouthWest();
    const mercY = (lat) => Math.log(Math.tan(Math.PI / 4 + (lat * Math.PI) / 360));
    const yTop = mercY(ne.lat());
    const ySpan = yTop - mercY(sw.lat());
    const xLeft = sw.lng();
    const xSpan = ne.lng() - sw.lng();
    if (!xSpan || !ySpan) return;
    const ids = houses
      .filter((h) => {
        const px = ((h.lon - xLeft) / xSpan) * W;
        const py = ((yTop - mercY(h.lat)) / ySpan) * H;
        return px >= rect.left && px <= rect.right && py >= rect.top && py <= rect.bottom;
      })
      .map((h) => h.id);
    handleBoxSelect(ids);
  }, [snapshot, handleBoxSelect]);

  // Esc exits box-select mode.
  useEffect(() => {
    if (!boxSelectMode) return;
    const onKey = (e) => {
      if (e.key === 'Escape') setBoxSelectMode(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [boxSelectMode]);

  // Save unsaved dark-red status flags — credited to the selected worker
  // (they attempted these houses, so they count like deliveries while
  // rendering dark red).
  const saveStatusPending = async (userId) => {
    for (const st of STATUS_TYPES) {
      const ids = Array.from(pendingStatusIds[st]);
      if (ids.length === 0) continue;
      const res = await safeJsonFetch(`${API}/api/admin/jobs/${jobId}/mark/save-status-houses`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: st, house_ids: ids, user_id: userId }),
      });
      if (!res.ok) {
        toast.error(res.data?.detail || res.error || `Saving ${STATUS_LABELS[st]} flags failed`);
        return false;
      }
      toast.success(`Saved ${ids.length} ${STATUS_LABELS[st]} flag${ids.length === 1 ? '' : 's'}${userId ? ' — credited' : ''}`);
    }
    setPendingStatusIds(emptyStatusSets());
    return true;
  };

  // "Save marks" → credit-confirmation dialog → batch POSTs. Deliveries
  // and status flags are BOTH credited to the chosen worker.
  const handleSavePending = async () => {
    if (!creditUserId) {
      toast.error('Pick which user should be credited first.');
      return;
    }
    setSavingHouses(true);
    if (pendingHouseIds.size > 0) {
      const res = await safeJsonFetch(`${API}/api/admin/jobs/${jobId}/mark/save-houses`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: creditUserId,
          house_ids: Array.from(pendingHouseIds),
        }),
      });
      if (!res.ok) {
        setSavingHouses(false);
        toast.error(res.data?.detail || res.error || 'Save failed');
        return;
      }
      const creditUser = contributors.find((c) => c.user_id === creditUserId);
      toast.success(
        `Saved ${res.data?.added ?? pendingHouseIds.size} deliver${(res.data?.added ?? 2) === 1 ? 'y' : 'ies'} — credited to ${creditUser?.user_name || 'selected user'}`,
      );
      setPendingHouseIds(new Set());
    }
    // Status flags ride along in the same save, credited to the same user.
    if (statusPendingCount > 0) {
      const ok = await saveStatusPending(creditUserId);
      if (!ok) {
        setSavingHouses(false);
        return;
      }
    }
    setSavingHouses(false);
    setSaveDialogOpen(false);
    // Re-fetch snapshot + contributors so counts and tooltips are authoritative.
    await loadAll();
  };

  // Save entry point: everything (deliveries AND status flags) goes
  // through the credit dialog — flagged houses are credited too.
  const handleSaveClick = () => {
    if (pendingHouseIds.size === 0 && statusPendingCount === 0) return;
    setSaveDialogOpen(true);
  };

  // Manual delivery marks passed to TrackPageGoogleMap (saved legacy
  // purple marks from the snapshot). Per-mark `is_hidden` flag (admin-only
  // legacy marks) suppresses them from the map view without deleting the
  // underlying record.
  const allMarksForMap = useMemo(() => {
    return (snapshot?.marks || [])
      .filter((m) => !m.is_hidden)
      .map((m) => ({
        id: m.id,
        lat: m.lat,
        lng: m.lng,
        timestamp: m.timestamp,
        flagged: !!m.flagged,
        nearest_house_m: m.nearest_house_m,
      }));
  }, [snapshot]);

  // Saved deliveries + local drafts render identically (yellow). The map
  // receives the union; handleHouseClick branches on which set a house is in.
  const combinedDelivered = useMemo(() => {
    if (pendingHouseIds.size === 0) return deliveredHouseIds;
    return new Set([...deliveredHouseIds, ...pendingHouseIds]);
  }, [deliveredHouseIds, pendingHouseIds]);

  // Dark-red status flags for the map: {house_id: 'non_deliverable'|'gated'}
  // (saved + unsaved drafts render identically).
  const statusHouseInfoForMap = useMemo(() => {
    const out = {};
    for (const st of STATUS_TYPES) {
      statusHouseIds[st].forEach((id) => { out[id] = st; });
      pendingStatusIds[st].forEach((id) => { out[id] = st; });
    }
    return out;
  }, [statusHouseIds, pendingStatusIds]);

  // GPS trails with one stable color PER USER (their live tracking doc and
  // every historical session share the same color).
  const TRAIL_PALETTE = ['#2563eb', '#dc2626', '#059669', '#d97706', '#7c3aed', '#0891b2', '#db2777'];
  const routeTrails = useMemo(() => {
    const trails = snapshot?.route_trails || [];
    const colorByUser = {};
    let idx = 0;
    trails.forEach((t) => {
      const uid = t.user_id || 'unknown';
      if (!(uid in colorByUser)) {
        colorByUser[uid] = TRAIL_PALETTE[idx % TRAIL_PALETTE.length];
        idx += 1;
      }
    });
    return trails.map((t, i) => ({
      id: `${t.user_id || 'u'}-${t.source}-${i}`,
      user_id: t.user_id,
      user_name: t.user_name,
      points: t.points,
      color: colorByUser[t.user_id || 'unknown'],
    }));
  }, [snapshot]); // eslint-disable-line react-hooks/exhaustive-deps

  // One legend entry per user.
  const trailLegend = useMemo(() => {
    const seen = new Map();
    routeTrails.forEach((t) => {
      if (!seen.has(t.user_id)) seen.set(t.user_id, { name: t.user_name, color: t.color });
    });
    return Array.from(seen.values());
  }, [routeTrails]);

  // Convert delivery_zone (which is sometimes [{lat,lng}], sometimes [[lat,lng]])
  // into the [[lat,lng]] shape TrackPageGoogleMap expects.
  const deliveryBoundary = useMemo(() => {
    const zone = snapshot?.delivery_zone;
    if (!zone || !Array.isArray(zone)) return null;
    return zone
      .map((p) => {
        if (Array.isArray(p) && p.length >= 2) return [p[0], p[1]];
        if (p && typeof p === 'object' && 'lat' in p && ('lng' in p || 'lon' in p)) {
          return [p.lat, p.lng ?? p.lon];
        }
        return null;
      })
      .filter(Boolean);
  }, [snapshot]);

  const mapCenter = useMemo(() => {
    // Priority chain for the initial map center, best signal first:
    // 1. Bbox center of the delivery zone polygon (if drawn)
    // 2. Bbox center of every existing manual mark on this job
    // 3. The job's saved `map_center` (set when the job was created)
    // 4. The Houston default (last-resort sentinel)
    if (deliveryBoundary && deliveryBoundary.length > 0) {
      const lats = deliveryBoundary.map((p) => p[0]);
      const lngs = deliveryBoundary.map((p) => p[1]);
      return {
        lat: lats.reduce((a, b) => a + b, 0) / lats.length,
        lng: lngs.reduce((a, b) => a + b, 0) / lngs.length,
      };
    }
    const marks = snapshot?.marks || [];
    if (marks.length > 0) {
      const lats = marks.map((m) => m.lat).filter((v) => typeof v === 'number');
      const lngs = marks.map((m) => m.lng).filter((v) => typeof v === 'number');
      if (lats.length && lngs.length) {
        return {
          lat: lats.reduce((a, b) => a + b, 0) / lats.length,
          lng: lngs.reduce((a, b) => a + b, 0) / lngs.length,
        };
      }
    }
    const mc = snapshot?.map_center;
    if (Array.isArray(mc) && mc.length === 2 && typeof mc[0] === 'number') {
      return { lat: mc[0], lng: mc[1] };
    }
    return { lat: 29.7604, lng: -95.3698 };
  }, [deliveryBoundary, snapshot]);

  // Once the map exists AND the snapshot is loaded, fit to the best
  // available bounds:
  //   1. If there's a zone polygon, the existing TrackPageGoogleMap effect
  //      already fits to it — nothing to do here.
  //   2. Else if there are existing marks, fit to their bbox so the admin
  //      sees every previous mark on screen at once.
  //   3. Else if the job has no zone and no marks but a `location` text
  //      is present, geocode it and pan there.
  // Each branch runs at most once per snapshot (refs guard re-triggering).
  const didAutoFitRef = useRef(false);
  const didAutoGeocodeRef = useRef(false);
  useEffect(() => {
    if (!mapInstanceRef.current || !snapshot) return;
    const haveZone = Array.isArray(deliveryBoundary) && deliveryBoundary.length >= 3;
    const marks = (snapshot.marks || []).filter((m) => typeof m?.lat === 'number' && typeof m?.lng === 'number');

    if (haveZone) return;  // existing map effect handles this

    if (marks.length > 0 && !didAutoFitRef.current && window.google?.maps?.LatLngBounds) {
      didAutoFitRef.current = true;
      const bounds = new window.google.maps.LatLngBounds();
      marks.forEach((m) => bounds.extend({ lat: m.lat, lng: m.lng }));
      mapInstanceRef.current.fitBounds(bounds, 60);
      return;
    }

    if (marks.length === 0
        && !didAutoGeocodeRef.current
        && snapshot.location
        && window.google?.maps?.Geocoder) {
      didAutoGeocodeRef.current = true;
      try {
        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode({ address: snapshot.location }, (results, status) => {
          if (status === 'OK' && results?.[0]?.geometry?.location && mapInstanceRef.current) {
            mapInstanceRef.current.panTo(results[0].geometry.location);
            mapInstanceRef.current.setZoom(15);
          }
        });
      } catch (_) { /* swallow — fallback center still works */ }
    }
  }, [snapshot, deliveryBoundary]);

  const handleAddressSearch = async (e) => {
    e?.preventDefault?.();
    const address = (searchInput || '').trim();
    if (!address) return;
    if (!window.google?.maps?.Geocoder) {
      toast.error('Map is still loading — try again in a second.');
      return;
    }
    setSearching(true);
    try {
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ address }, (results, status) => {
        setSearching(false);
        if (status !== 'OK' || !results?.[0]?.geometry?.location) {
          toast.error(`Address not found (${status || 'unknown error'})`);
          return;
        }
        const loc = results[0].geometry.location;
        if (mapInstanceRef.current) {
          mapInstanceRef.current.panTo(loc);
          mapInstanceRef.current.setZoom(17);
        }
        toast.success(`Map centered on ${results[0].formatted_address}`);
      });
    } catch (err) {
      setSearching(false);
      toast.error(`Geocoder error: ${err.message || err}`);
    }
  };

  const handleRecenter = () => {
    if (!mapInstanceRef.current) return;
    if (deliveryBoundary && deliveryBoundary.length >= 3) {
      const bounds = new window.google.maps.LatLngBounds();
      deliveryBoundary.forEach((p) => bounds.extend({ lat: p[0], lng: p[1] }));
      mapInstanceRef.current.fitBounds(bounds, 30);
      return;
    }
    mapInstanceRef.current.panTo(mapCenter);
    mapInstanceRef.current.setZoom(15);
  };

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center bg-gray-50">
        <div className="w-8 h-8 border-4 border-purple-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (error) {
    return (
      <div className="h-screen flex flex-col items-center justify-center gap-4 bg-gray-50 p-6">
        <AlertTriangle className="w-12 h-12 text-red-500" />
        <p className="text-gray-700">{error}</p>
        <Button onClick={() => navigate('/admin/jobs')} data-testid="admin-mark-back-error-btn">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back to Jobs
        </Button>
      </div>
    );
  }

  const creditedUser = contributors.find((c) => c.user_id === creditUserId);

  return (
    <div className="h-[calc(100vh-64px)] flex flex-col bg-white" data-testid="admin-mark-page">
      {/* Top bar */}
      <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 bg-white">
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              // eslint-disable-next-line no-alert
              if ((pendingHouseIds.size + statusPendingCount) > 0 && !window.confirm(`Discard ${pendingHouseIds.size + statusPendingCount} unsaved mark${(pendingHouseIds.size + statusPendingCount) === 1 ? '' : 's'} and leave?`)) return;
              navigate('/admin/jobs');
            }}
            data-testid="admin-mark-back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <div>
            <h1 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <MapPin className="w-5 h-5 text-purple-600" />
              Admin Mark — {snapshot?.job_title}
            </h1>
            <p className="text-xs text-gray-500">
              Click green (undelivered) house dots to draft them as delivered, then
              hit <span className="font-semibold">Save deliveries</span> and pick who
              to credit. Click a saved yellow dot to un-mark it (yellow dots include
              deliveries completed by workers).
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {routeTrails.length > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowTrails((v) => !v)}
              className={showTrails ? 'border-blue-300 text-blue-700 bg-blue-50 hover:bg-blue-100' : 'border-gray-300 text-gray-500 hover:bg-gray-50'}
              title="Toggle worker GPS trail lines on the map"
              data-testid="admin-mark-trails-toggle-btn"
            >
              {showTrails ? <Eye className="w-4 h-4 mr-1.5" /> : <EyeOff className="w-4 h-4 mr-1.5" />}
              GPS trails
            </Button>
          )}
          <Button
            variant={boxSelectMode ? 'default' : 'outline'}
            size="sm"
            onClick={() => setBoxSelectMode((v) => !v)}
            className={boxSelectMode ? 'bg-purple-600 hover:bg-purple-700 text-white' : 'border-purple-300 text-purple-700 hover:bg-purple-50'}
            title="Drag a rectangle on the map to draft every green house inside it (Esc to exit)"
            data-testid="admin-mark-box-select-btn"
          >
            <BoxSelect className="w-4 h-4 mr-1.5" />
            {boxSelectMode ? 'Box select: ON' : 'Box select'}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setStatusMode((m) => (m === 'non_deliverable' ? null : 'non_deliverable'))}
            className={statusMode === 'non_deliverable'
              ? 'bg-red-900 hover:bg-red-950 text-white border-red-900'
              : 'border-red-300 text-red-800 hover:bg-red-50'}
            title="Check this, then box-select (or click) houses to flag them Non Deliverable — they render dark red"
            data-testid="admin-mark-non-deliverable-btn"
          >
            {statusMode === 'non_deliverable' ? <CheckSquare className="w-4 h-4 mr-1.5" /> : <Square className="w-4 h-4 mr-1.5" />}
            Non Deliverable
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setStatusMode((m) => (m === 'gated' ? null : 'gated'))}
            className={statusMode === 'gated'
              ? 'bg-red-900 hover:bg-red-950 text-white border-red-900'
              : 'border-red-300 text-red-800 hover:bg-red-50'}
            title="Check this, then box-select (or click) houses to flag them Gated Community — they render dark red"
            data-testid="admin-mark-gated-btn"
          >
            {statusMode === 'gated' ? <CheckSquare className="w-4 h-4 mr-1.5" /> : <Square className="w-4 h-4 mr-1.5" />}
            Gated Community
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setStatusMode((m) => (m === 'safety' ? null : 'safety'))}
            className={statusMode === 'safety'
              ? 'bg-red-900 hover:bg-red-950 text-white border-red-900'
              : 'border-red-300 text-red-800 hover:bg-red-50'}
            title="Check this, then box-select (or click) houses to flag them as a Safety concern (not safe for delivery) — they render dark red"
            data-testid="admin-mark-safety-btn"
          >
            {statusMode === 'safety' ? <CheckSquare className="w-4 h-4 mr-1.5" /> : <Square className="w-4 h-4 mr-1.5" />}
            Safety
          </Button>
          {(pendingHouseIds.size + statusPendingCount) > 0 && (
            <>
              <Badge
                className="bg-purple-100 text-purple-700 border border-purple-300"
                data-testid="admin-mark-unsaved-badge"
              >
                {pendingHouseIds.size + statusPendingCount} unsaved
              </Badge>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleDiscardPending}
                className="text-gray-500 hover:text-red-600"
                data-testid="admin-mark-discard-btn"
              >
                <Trash2 className="w-3.5 h-3.5 mr-1" /> Discard
              </Button>
            </>
          )}
          <Button
            size="sm"
            onClick={() => {
              if (pendingHouseIds.size === 0 && statusPendingCount === 0) {
                toast.error('Click or box-select house dots first.');
                return;
              }
              handleSaveClick();
            }}
            disabled={(pendingHouseIds.size + statusPendingCount) === 0 || savingHouses}
            className="bg-purple-600 hover:bg-purple-700 text-white"
            data-testid="admin-mark-save-btn"
          >
            <Save className="w-4 h-4 mr-1.5" />
            Save{(pendingHouseIds.size + statusPendingCount) > 0 ? ` ${pendingHouseIds.size + statusPendingCount}` : ''} mark{(pendingHouseIds.size + statusPendingCount) === 1 ? '' : 's'}
          </Button>
          <Badge className="bg-amber-100 text-amber-700 border border-amber-300">
            {deliveredHouseIds.size} delivered
          </Badge>
          {STATUS_TYPES.reduce((n, st) => n + statusHouseIds[st].size, 0) > 0 && (
            <Badge
              className="bg-red-900 text-white border border-red-950"
              data-testid="admin-mark-flagged-badge"
            >
              {STATUS_TYPES.reduce((n, st) => n + statusHouseIds[st].size, 0)} flagged
            </Badge>
          )}
          {(snapshot?.marks_count || 0) > 0 && (
            <Badge className="bg-purple-100 text-purple-700 border border-purple-300">
              {snapshot.marks_count} legacy purple
            </Badge>
          )}
        </div>
      </div>

      {/* Body — sidebar + map */}
      <div className="flex-1 flex min-h-0">
        {/* Sidebar */}
        <aside className="w-80 border-r border-gray-200 p-4 flex flex-col gap-4 overflow-y-auto bg-gray-50">
          {/* Address search — admin can jump anywhere */}
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1.5">
              <Search className="w-3 h-3 inline mr-1" /> Find Address
            </label>
            <form onSubmit={handleAddressSearch} className="flex gap-1.5" data-testid="admin-mark-search-form">
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder={snapshot?.location || 'e.g. 100 Main St, Cupertino CA'}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-purple-400"
                data-testid="admin-mark-search-input"
              />
              <Button
                type="submit"
                size="sm"
                disabled={searching || !searchInput.trim()}
                className="bg-gray-700 hover:bg-gray-800 text-white"
                data-testid="admin-mark-search-btn"
              >
                {searching ? '...' : 'Go'}
              </Button>
            </form>
            <Button
              variant="outline"
              size="sm"
              onClick={handleRecenter}
              className="w-full mt-2 text-gray-700"
              data-testid="admin-mark-recenter-btn"
            >
              <Crosshair className="w-3 h-3 mr-1" /> Recenter on zone
            </Button>
          </div>

          <div>
            <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1.5">
              <Users className="w-3 h-3 inline mr-1" /> Credit Marks To
            </label>
            {isPostCompletionFallback && (
              <p
                className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 mb-1.5 leading-snug"
                data-testid="post-completion-hint"
              >
                This job is closed — the credit list shows every active worker
                because there's no longer a live team. Pick the worker who
                actually delivered these houses.
              </p>
            )}
            <select
              value={creditUserId}
              onChange={(e) => setCreditUserId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-purple-400"
              data-testid="admin-mark-credit-user-select"
            >
              {contributors.length === 0 && (
                <option value="">No contributors found for this job</option>
              )}
              {contributors.map((c) => (
                <option key={c.user_id} value={c.user_id}>
                  {c.user_name}
                  {c.is_currently_assigned ? ' (currently assigned)' : ''}
                  {c.is_fallback ? ' (fallback)' : ''}
                  {' — '}
                  {c.deliveries_count} deliveries
                  {c.manual_marks > 0 ? `, ${c.manual_marks} marks` : ''}
                </option>
              ))}
            </select>
            {creditedUser && (
              <p className="mt-1.5 text-xs text-gray-500">
                Total credited to <span className="font-semibold text-purple-700">{creditedUser.user_name}</span>:{' '}
                <span className="font-semibold">{creditedUser.deliveries_count}</span> deliveries
                {creditedUser.manual_marks > 0 ? `, ${creditedUser.manual_marks} legacy purple marks` : ''}.
              </p>
            )}
          </div>

          {/* Snap legacy purple marks → houses. Surfaces only when this job
              actually has free-floating purple marks worth cleaning up. */}
          {(snapshot?.marks_count || 0) > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={async () => {
                // eslint-disable-next-line no-alert
                if (!window.confirm(
                  `Snap legacy purple admin marks onto the nearest house dot (within ~30 m). Marks farther away stay purple. Continue?`
                )) return;
                const res = await safeJsonFetch(
                  `${API}/api/admin/jobs/${jobId}/mark/snap-legacy-marks`,
                  { method: 'POST', headers: authHeaders() },
                );
                if (!res.ok) {
                  toast.error(res.data?.detail || 'Snap failed');
                  return;
                }
                toast.success(
                  `Snapped ${res.data.snapped} marks · ${res.data.kept_purple} stayed purple`,
                );
                await loadAll();
              }}
              className="w-full text-purple-700 border-purple-300 hover:bg-purple-50"
              data-testid="snap-legacy-marks-btn"
            >
              Snap {snapshot?.marks_count} legacy purple mark{snapshot?.marks_count === 1 ? '' : 's'} → houses
            </Button>
          )}

          {/* Per-mark visibility toggle for legacy purple marks. Hidden marks
              still never appear on customer maps — this just lets the admin
              clean up their own view. Surfaces only the admin-only marks
              (the snap migration tags those) so we don't litter the
              sidebar with every worker tap. */}
          {(snapshot?.marks || []).filter((m) => m.is_admin_only).length > 0 && (
            <div className="rounded-md border bg-white p-2.5 text-xs space-y-1">
              <p className="font-semibold text-gray-700 mb-1">
                Legacy admin marks ({(snapshot.marks || []).filter((m) => m.is_admin_only).length})
                <span className="text-[10px] font-normal text-gray-400 ml-1">(never shown to clients)</span>
              </p>
              <div className="max-h-32 overflow-y-auto space-y-1">
                {(snapshot.marks || []).filter((m) => m.is_admin_only).map((m) => (
                  <button
                    key={m.id}
                    type="button"
                    onClick={async () => {
                      const res = await safeJsonFetch(
                        `${API}/api/admin/jobs/${jobId}/mark/${m.id}/toggle-hidden`,
                        { method: 'POST', headers: authHeaders() },
                      );
                      if (!res.ok) {
                        toast.error(res.data?.detail || 'Toggle failed');
                        return;
                      }
                      toast.success(res.data?.is_hidden ? 'Hidden from your view' : 'Shown again', { duration: 1200 });
                      await loadAll();
                    }}
                    className="w-full flex items-center justify-between px-2 py-1 rounded hover:bg-gray-50 text-left"
                    data-testid={`toggle-hidden-${m.id}`}
                  >
                    <span className="truncate text-gray-600">
                      {m.lat?.toFixed(5)}, {m.lng?.toFixed(5)}
                    </span>
                    {m.is_hidden ? (
                      <span className="flex items-center gap-1 text-gray-400 text-[10px]">
                        <EyeOff className="w-3 h-3" /> Hidden
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 text-purple-700 text-[10px]">
                        <Eye className="w-3 h-3" /> Shown
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
            <p className="font-semibold mb-1 flex items-center gap-1">
              <CheckCircle2 className="w-3 h-3" /> How this works
            </p>
            <ul className="list-disc pl-4 space-y-0.5">
              <li><span className="font-semibold">Click a green house</span> → drafts it as delivered (yellow, unsaved).</li>
              <li><span className="font-semibold">Hit Save deliveries</span> → pick who to credit, then everything saves at once.</li>
              <li><span className="font-semibold">Click an unsaved yellow house</span> → removes it from the draft.</li>
              <li><span className="font-semibold">Click a saved yellow house</span> → un-marks (after confirm).</li>
              <li><span className="font-semibold">Yellow dots</span> include deliveries completed by workers in the field.</li>
              <li><span className="font-semibold">Non Deliverable / Gated Community / Safety</span> — check one, then box-select or click houses to flag them <span className="font-semibold text-red-900">dark red</span>. On save they're credited to the worker you pick (attempted = counted). Click a dark-red house in the same mode to un-flag it.</li>
              <li>Empty land / roads aren't clickable. Saves are audit-logged.</li>
            </ul>
          </div>

          {!deliveryBoundary && (
            <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
              <p className="font-semibold mb-1 flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" /> No delivery zone
              </p>
              <p>This job has no saved delivery zone polygon, so the map will open with a default view. You can still drop marks anywhere.</p>
            </div>
          )}
        </aside>

        {/* Map */}
        <div className="flex-1 relative">
          <TrackPageGoogleMap
            mapCenter={mapCenter}
            deliveryBoundary={deliveryBoundary}
            houseLocations={snapshot?.cached_house_locations || []}
            deliveredHouses={combinedDelivered}
            deliveredHouseInfo={deliveredHouseInfo}
            statusHouseInfo={statusHouseInfoForMap}
            routeTrails={showTrails ? routeTrails : []}
            manualDeliveryLocations={allMarksForMap}
            onMapClick={handleMapClick}
            onHouseClick={handleHouseClick}
            onMapReady={handleMapReady}
            isTracking={false}
          />
          {/* Box-select drag layer + helper banner */}
          {boxSelectMode && <BoxSelectOverlay onSelect={handleBoxPixelSelect} />}
          {boxSelectMode && (
            <div className="pointer-events-none absolute top-3 left-1/2 -translate-x-1/2 z-20">
              <div
                className={`${statusMode ? 'bg-red-900' : 'bg-purple-600'} text-white px-4 py-2 rounded-full shadow-lg text-sm font-medium flex items-center gap-2`}
                data-testid="box-select-banner"
              >
                <BoxSelect className="w-4 h-4" />
                {statusMode
                  ? `Drag a box to flag houses as ${STATUS_LABELS[statusMode]} (dark red) — Esc to exit`
                  : 'Drag a box around houses to draft them — Esc to exit'}
              </div>
            </div>
          )}

          {/* GPS trail legend — one entry per worker */}
          {showTrails && trailLegend.length > 0 && (
            <div
              className="absolute bottom-4 left-4 z-10 bg-white/95 backdrop-blur rounded-lg shadow-lg border border-gray-200 px-3 py-2"
              data-testid="admin-mark-trail-legend"
            >
              <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500 mb-1">GPS Trails</p>
              <ul className="space-y-1">
                {trailLegend.map((entry, i) => (
                  <li key={i} className="flex items-center gap-2 text-xs text-gray-700">
                    <span
                      className="inline-block w-4 h-1 rounded-full"
                      style={{ backgroundColor: entry.color }}
                    />
                    {entry.name || 'Unknown worker'}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Hint overlay (only on truly untouched jobs) */}
          {pendingHouseIds.size === 0 && deliveredHouseIds.size === 0 && allMarksForMap.length === 0 && (
            <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
              <div className="bg-white/90 backdrop-blur px-4 py-2 rounded-full shadow text-sm text-gray-700 border border-gray-200">
                Click green house dots to draft deliveries, then hit "Save deliveries"
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Save-confirmation dialog — asks WHO to credit before committing
          the drafted deliveries in one batch. */}
      <Dialog open={saveDialogOpen} onOpenChange={(open) => { if (!savingHouses) setSaveDialogOpen(open); }}>
        <DialogContent className="max-w-md" data-testid="save-credit-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Save className="w-5 h-5 text-purple-600" />
              Save {pendingHouseIds.size + statusPendingCount} mark{(pendingHouseIds.size + statusPendingCount) === 1 ? '' : 's'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 pt-1">
            <p className="text-sm text-gray-600">
              Who should these be credited to? Deliveries
              {statusPendingCount > 0 ? ' and dark-red flags (Non Deliverable / Gated Community / Safety)' : ''} count
              toward that worker's delivery total — flagged houses were attempted by the worker,
              so they're credited too while staying dark red on the map.
            </p>
            {(pendingHouseIds.size > 0 || statusPendingCount > 0) && (
              <div className="text-xs text-gray-600 space-y-0.5" data-testid="save-credit-breakdown">
                {pendingHouseIds.size > 0 && <div>• {pendingHouseIds.size} deliver{pendingHouseIds.size === 1 ? 'y' : 'ies'}</div>}
                {pendingStatusIds.non_deliverable.size > 0 && <div>• {pendingStatusIds.non_deliverable.size} Non Deliverable flag{pendingStatusIds.non_deliverable.size === 1 ? '' : 's'}</div>}
                {pendingStatusIds.gated.size > 0 && <div>• {pendingStatusIds.gated.size} Gated Community flag{pendingStatusIds.gated.size === 1 ? '' : 's'}</div>}
                {pendingStatusIds.safety.size > 0 && <div>• {pendingStatusIds.safety.size} Safety flag{pendingStatusIds.safety.size === 1 ? '' : 's'}</div>}
              </div>
            )}
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1.5">
                <Users className="w-3 h-3 inline mr-1" /> Credit deliveries to
              </label>
              <select
                value={creditUserId}
                onChange={(e) => setCreditUserId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-purple-400"
                data-testid="save-credit-user-select"
              >
                {contributors.length === 0 && (
                  <option value="">No contributors found for this job</option>
                )}
                {contributors.map((c) => (
                  <option key={c.user_id} value={c.user_id}>
                    {c.user_name}
                    {c.is_currently_assigned ? ' (currently assigned)' : ''}
                    {c.is_fallback ? ' (fallback)' : ''}
                    {' — '}
                    {c.deliveries_count} deliveries
                  </option>
                ))}
              </select>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-md px-3 py-2 text-xs text-amber-900">
              This saves all {pendingHouseIds.size + statusPendingCount} drafted mark{(pendingHouseIds.size + statusPendingCount) === 1 ? '' : 's'} in
              one shot and is audit-logged.
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setSaveDialogOpen(false)}
              disabled={savingHouses}
              data-testid="save-credit-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSavePending}
              disabled={savingHouses || !creditUserId}
              className="bg-purple-600 hover:bg-purple-700 text-white"
              data-testid="save-credit-confirm-btn"
            >
              {savingHouses ? 'Saving…' : 'Confirm & Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminMarkJob;
